import {
  ALL_IMAGE_INPUT_MIME_TYPES,
  inferImageInputMimeTypeFromPath,
  normalizeImageInputMimeType,
} from './tool-contracts/input-formats';

const DIRECT_UPLOAD_MIME_TYPES = new Set<string>(ALL_IMAGE_INPUT_MIME_TYPES);

const NORMALIZATION_ONLY_MIME_BY_ALIAS: Record<string, string> = {
  'image/avif': 'image/avif',
  'image/heic': 'image/heic',
  'image/heic-sequence': 'image/heic',
  'image/heif': 'image/heif',
  'image/heif-sequence': 'image/heif',
  'image/svg+xml': 'image/svg+xml',
  'image/tif': 'image/tiff',
  'image/tiff': 'image/tiff',
  'image/x-tif': 'image/tiff',
  'image/x-tiff': 'image/tiff',
};

const NORMALIZATION_ONLY_MIME_BY_EXTENSION: Record<string, string> = {
  avif: 'image/avif',
  heic: 'image/heic',
  heif: 'image/heif',
  svg: 'image/svg+xml',
  tif: 'image/tiff',
  tiff: 'image/tiff',
};

const NORMALIZATION_ONLY_MIME_TYPES = new Set(
  Object.values(NORMALIZATION_ONLY_MIME_BY_ALIAS)
);

export type DeclaredImageFile = {
  name: string;
  type: string;
};

function readFileExtension(fileName: string) {
  const normalized = fileName.trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf('.');
  if (dotIndex < 0 || dotIndex === normalized.length - 1) {
    return '';
  }

  return normalized.slice(dotIndex + 1);
}

function normalizeMimeType(value: string) {
  return value.split(';')[0]?.trim().toLowerCase() ?? '';
}

export function resolveDeclaredImageMimeType(file: DeclaredImageFile) {
  const declaredType = normalizeMimeType(file.type);
  const canonicalDeclaredType =
    normalizeImageInputMimeType(declaredType) ??
    NORMALIZATION_ONLY_MIME_BY_ALIAS[declaredType];
  if (canonicalDeclaredType) {
    return canonicalDeclaredType;
  }

  const canonicalExtensionType =
    inferImageInputMimeTypeFromPath(file.name) ??
    NORMALIZATION_ONLY_MIME_BY_EXTENSION[readFileExtension(file.name)];
  if (canonicalExtensionType) {
    return canonicalExtensionType;
  }

  return declaredType.startsWith('image/') ? declaredType : null;
}

export function resolveSupportedMimeTypes(values?: readonly string[]) {
  if (!values?.length) {
    return DIRECT_UPLOAD_MIME_TYPES;
  }

  return new Set(
    values
      .map((value) => {
        const normalized = normalizeMimeType(value);
        return (
          normalizeImageInputMimeType(normalized) ??
          NORMALIZATION_ONLY_MIME_BY_ALIAS[normalized] ??
          normalized
        );
      })
      .filter((value): value is string => Boolean(value))
  );
}

export function canEnhancerUploadFileReachSupportedMimeType(
  file: DeclaredImageFile,
  supportedMimeTypes?: readonly string[]
) {
  const declaredMimeType = resolveDeclaredImageMimeType(file);
  if (!declaredMimeType) {
    return false;
  }

  const supported = resolveSupportedMimeTypes(supportedMimeTypes);
  if (
    supported.has(declaredMimeType) &&
    !requiresFormatNormalization(file, supportedMimeTypes)
  ) {
    return true;
  }

  return (
    (DIRECT_UPLOAD_MIME_TYPES.has(declaredMimeType) ||
      NORMALIZATION_ONLY_MIME_TYPES.has(declaredMimeType)) &&
    (supported.has('image/webp') || supported.has('image/png'))
  );
}

export function resolveEnhancerBrowserUploadAccept(
  supportedMimeTypes?: readonly string[]
) {
  const supported = resolveSupportedMimeTypes(supportedMimeTypes);
  return supported.has('image/webp') || supported.has('image/png')
    ? 'image/*'
    : (supportedMimeTypes ?? []).join(',');
}

export function requiresFormatNormalization(
  file: DeclaredImageFile,
  supportedMimeTypes?: readonly string[]
) {
  const declaredMimeType = resolveDeclaredImageMimeType(file);
  if (!declaredMimeType) {
    return false;
  }

  if (
    !DIRECT_UPLOAD_MIME_TYPES.has(declaredMimeType) ||
    declaredMimeType === 'image/gif' ||
    declaredMimeType === 'image/svg+xml'
  ) {
    return true;
  }

  return !resolveSupportedMimeTypes(supportedMimeTypes).has(declaredMimeType);
}

export function shouldLoadEnhancerUploadPreprocessor(
  file: DeclaredImageFile,
  supportedMimeTypes?: readonly string[]
) {
  const declaredMimeType = resolveDeclaredImageMimeType(file);
  return (
    declaredMimeType === 'image/png' ||
    declaredMimeType === 'image/webp' ||
    requiresFormatNormalization(file, supportedMimeTypes)
  );
}

export function resolveNormalizationOutputMimeType(
  supportedMimeTypes: ReadonlySet<string>
) {
  if (supportedMimeTypes.has('image/webp')) {
    return 'image/webp' as const;
  }
  if (supportedMimeTypes.has('image/png')) {
    return 'image/png' as const;
  }

  throw new Error('normalized-image-type-not-supported-by-tool');
}
